import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { useConfirm } from '../../../../../app/hooks/useConfirm';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../app/store/configureStore';
import {
  fetchMenuListAsync,
  deleteMenuAsync,
  setMenus,
  updateMenuOrderAsync,
} from '../../store/menuSlice';
import EmptyScreen from '../../../../../app/components/EmptyScreen';
import LoadingComponent from '../../../../../app/layout/LoadingComponent';
import { ConfirmDialog } from '../../../../../app/components/ConfirmDialog';
import ListTitleBox from '../../../../../app/components/list/ListTitleBox';
import { MenuHeader } from './MenuHeader';
import Menu from '../../../../../app/data/models/menu';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { MenuTableRow } from './MenuTableRow';
import ErrorComponent from '../../../../../app/layout/ErrorComponent';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

export const MenuList: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{
    restaurantId: string;
  }>();
  const { menus, menuListStatus } = useAppSelector((state) => state.menus);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const { confirm } = useConfirm();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasChanges && currentLocation.pathname !== nextLocation.pathname
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = menus.findIndex((menu) => menu.id === active.id);
        const newIndex = menus.findIndex((menu) => menu.id === over?.id);

        const newOrderedMenus = arrayMove(menus, oldIndex, newIndex);

        // Update local state immediately for responsiveness
        dispatch(setMenus(newOrderedMenus));
        setHasChanges(true);
      }

      setActiveId(null);
    },
    [menus, dispatch]
  );

  const onCreateClick = useCallback(() => {
    navigate(`/manager/restaurants/${restaurantId}/menus/create`);
  }, [navigate, restaurantId]);

  useEffect(() => {
    dispatch(fetchMenuListAsync(restaurantId));
  }, [restaurantId, dispatch]);

  const onSaveAction = useCallback(async () => {
    setSaving(true);
    try {
      const orderedMenuIds = menus.map((menu) => menu.id);
      await dispatch(
        updateMenuOrderAsync({ restaurantId, orderedMenuIds })
      ).unwrap();
      toast.success(t('menu.reorder.success'));
      setHasChanges(false);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.title ||
            error.message ||
            t('menu.reorder.error')
        );
      } else {
        toast.error((error as Error).message || t('menu.reorder.error'));
      }
    } finally {
      setSaving(false);
    }
  }, [menus, dispatch, restaurantId, t]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const handleBlock = async () => {
        const confirmed = await confirm({
          title: t('common.discard-changes.title'),
          message: t('common.discard-changes.message'),
          confirmText: t('common.discard-changes.confirm'),
          cancelText: t('common.discard-changes.cancel'),
        });

        if (confirmed) {
          blocker.proceed();
        } else {
          blocker.reset();
        }
      };
      handleBlock();
    }
  }, [blocker, confirm, t]);

  const handleEditMenu = useCallback(
    async (menu: Menu) => {
      navigate(`/manager/restaurants/${restaurantId}/menus/${menu.id}/edit`);
    },
    [navigate, restaurantId]
  );

  const handleDeleteMenu = useCallback(
    async (menuId: string) => {
      setLoading(true);
      try {
        const result = await dispatch(
          deleteMenuAsync({ restaurantId, menuId })
        );
        if (deleteMenuAsync.fulfilled.match(result)) {
          toast.success(t('menu.delete.success'));
        }
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(
            error.response?.data?.title ||
              error.message ||
              t('menu.delete.error')
          );
        } else {
          toast.error((error as Error).message || t('menu.delete.error'));
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch, restaurantId, t]
  );

  const handleMenuClick = useCallback(
    (menu: Menu) => {
      navigate(`/manager/restaurants/${restaurantId}/menus/${menu.id}`);
    },
    [navigate, restaurantId]
  );

  const handleDeleteClick = useCallback((menuId: string) => {
    setMenuToDelete(menuId);
    setOpenConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (menuToDelete) {
      await handleDeleteMenu(menuToDelete);
      setOpenConfirm(false);
      setMenuToDelete(null);
    }
  }, [menuToDelete, handleDeleteMenu]);

  if (menuListStatus === 'loading')
    return (
      <LoadingComponent
        header={
          <ListTitleBox
            title={t('menu.list.title')}
            onPrimaryClick={() =>
              navigate(`/manager/restaurants/${restaurantId}/menus/create`)
            }
          />
        }
      />
    );

  if (menuListStatus === 'failed')
    return (
      <ErrorComponent
        header={
          <ListTitleBox
            title={t('menu.list.title')}
            onPrimaryClick={() =>
              navigate(`/manager/restaurants/${restaurantId}/menus/create`)
            }
          />
        }
        onPrimaryAction={() => dispatch(fetchMenuListAsync(restaurantId))}
      />
    );

  if (!menus || menus.length === 0)
    return (
      <EmptyScreen
        title={t('menu.list.title')}
        emptyText={t('menu.list.empty')}
        onPrimaryClick={() =>
          navigate(`/manager/restaurants/${restaurantId}/menus/create`)
        }
      />
    );

  return (
    <>
      <ListTitleBox
        title={t('menu.list.title')}
        primaryButtonText={hasChanges ? t('common.save') : t('common.create')}
        onPrimaryClick={hasChanges ? onSaveAction : onCreateClick}
        primaryButtonDisabled={hasChanges ? saving : false}
        primaryButtonProps={{
          loading: hasChanges ? saving : false,
        }}
      />
      <TableContainer
        component={Paper}
        sx={{ width: '100%', mb: 2, overflowX: 'auto' }}
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Table aria-label={t('menu.list.title')}>
            <TableHead>
              <MenuHeader />
            </TableHead>
            <TableBody>
              <SortableContext
                items={menus.map((menu) => menu.id)}
                strategy={verticalListSortingStrategy}
              >
                {menus.map((menu, index) => (
                  <MenuTableRow
                    key={menu.id}
                    menu={menu}
                    index={index}
                    loading={loading}
                    onMenuClick={handleMenuClick}
                    onEditClick={handleEditMenu}
                    onDeleteClick={handleDeleteClick}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
          <DragOverlay>
            {activeId ? (
              <MenuTableRow
                menu={menus.find((menu) => menu.id === activeId)!}
                index={menus.findIndex((menu) => menu.id === activeId)}
                loading={false}
                onMenuClick={() => {}}
                onEditClick={() => {}}
                onDeleteClick={() => {}}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </TableContainer>
      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('menu.delete.confirm.title')}
        message={t('menu.delete.confirm.message')}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
